import { useMemo } from 'react';
import { useQuery } from '@apollo/client/react';
import { Link, useNavigate } from 'react-router';
import type { ColumnDef, SortingState, Updater } from '@tanstack/react-table';
import { graphql } from '@/gql';
import type { NightFilter } from '@/gql/graphql';
import { Badge } from '@/components/ui/Badge';
import { DataGrid } from '@/components/ui/DataGrid';
import { FilterBar } from '@/components/ui/FilterBar';
import { QueryErrorNotice } from '@/components/ui/QueryErrorNotice';
import { Switch } from '@/components/ui/Switch';
import {
  formatCents,
  formatDateTimeAmsterdam,
  formatInteger,
} from '@/lib/format';
import { hasPermission, usePermissions } from '@/lib/permissions';
import { useListHref } from '@/hooks/useListLocation';
import { sortingFromUrl, sortingToUrl, useUrlState } from '@/hooks/useUrlState';
import {
  NIGHT_COLUMN_SORT,
  NIGHTS_GRID_DEFAULT_SORT,
  nightsGridUrlSchema,
} from './nightsGridUrlState';
import { NightsViewToggle } from './NightsViewToggle';

const NightsGridDoc = graphql(/* GraphQL */ `
  query NightsGrid(
    $filter: NightFilter
    $sort: NightSort
    $page: Int
    $pageSize: Int
  ) {
    nights(filter: $filter, sort: $sort, page: $page, pageSize: $pageSize) {
      pageInfo {
        totalRows
        totalPages
        page
        pageSize
      }
      nodes {
        id
        title
        doorsOpen
        doorsClose
        isActive
        published
        soldTickets
        guestTickets
        ticketRevenueCents
      }
    }
  }
`);

interface NightRow {
  id: string;
  title: string;
  doorsOpen: string;
  doorsClose: string;
  isActive: boolean;
  published: boolean;
  soldTickets: number;
  guestTickets: number;
  ticketRevenueCents?: string | number | null;
}

/**
 * The Nights grid (`/nights?layout=grid`): the upcoming nights (doors close still ahead), the next night
 * first. Ticketed only and Active only are ON by default and written to the URL only when switched off; a filter
 * that is off is left out of the query (sending `false` would list only door-only or only inactive nights).
 * Cell ids are `admin.nights.grid.<column>.<nightId>`, the rows `admin.nights.grid.row.<nightId>`.
 */
export function NightsGridPage() {
  const navigate = useNavigate();
  const listHref = useListHref();
  const { state: url, patch, setFilters } = useUrlState(nightsGridUrlSchema);
  // Go answers null revenue to a caller without report:chart:read: the column is not shown for them.
  const showRevenue = hasPermission(usePermissions(), 'report:chart:read');
  const ticketedOnly = url.ticketed !== 'all';
  const activeOnly = url.active !== 'all';

  const sorting = useMemo(() => sortingFromUrl(url), [url]);
  const handleSortingChange = (updater: Updater<SortingState>) => {
    const next = typeof updater === 'function' ? updater(sorting) : updater;
    setFilters(sortingToUrl(next, NIGHTS_GRID_DEFAULT_SORT));
  };
  const sort = useMemo(
    () => ({
      field: NIGHT_COLUMN_SORT[url.sort],
      direction: (url.dir === 'desc' ? 'DESC' : 'ASC') as 'ASC' | 'DESC',
    }),
    [url.sort, url.dir],
  );

  const filter = useMemo<NightFilter>(
    () => ({
      upcoming: true,
      ...(ticketedOnly ? { requireTicket: true } : {}),
      ...(activeOnly ? { isActive: true } : {}),
    }),
    [ticketedOnly, activeOnly],
  );

  const { data, previousData, loading, error } = useQuery(NightsGridDoc, {
    variables: { filter, sort, page: url.page, pageSize: url.size },
    errorPolicy: 'all',
  });
  const viewData = data ?? previousData;
  const rows: NightRow[] = useMemo(
    () => viewData?.nights.nodes ?? [],
    [viewData],
  );
  const pageInfo = viewData?.nights.pageInfo;

  const columns = useMemo<ColumnDef<NightRow>[]>(() => {
    const cols: ColumnDef<NightRow>[] = [
      {
        id: 'title',
        accessorKey: 'title',
        header: 'Night title',
        cell: ({ row }) => (
          <Link
            id={`admin.nights.grid.title.${row.original.id}`}
            to={`/nights/${row.original.id}`}
            state={{ from: listHref }}
            className="hover:text-brand font-medium text-gray-900 dark:text-white"
          >
            {row.original.title}
          </Link>
        ),
      },
      {
        id: 'doorsOpen',
        accessorKey: 'doorsOpen',
        header: 'Doors open',
        cell: ({ row }) => (
          <span id={`admin.nights.grid.doorsOpen.${row.original.id}`}>
            {formatDateTimeAmsterdam(row.original.doorsOpen)}
          </span>
        ),
      },
      {
        id: 'doorsClose',
        accessorKey: 'doorsClose',
        header: 'Doors close',
        cell: ({ row }) => (
          <span id={`admin.nights.grid.doorsClose.${row.original.id}`}>
            {formatDateTimeAmsterdam(row.original.doorsClose)}
          </span>
        ),
      },
      {
        id: 'active',
        header: 'Active',
        enableSorting: false,
        cell: ({ row }) => (
          <Badge
            id={`admin.nights.grid.active.${row.original.id}`}
            tone={row.original.isActive ? 'success' : 'neutral'}
          >
            {row.original.isActive ? 'Active' : 'Inactive'}
          </Badge>
        ),
      },
      {
        id: 'published',
        header: 'Published',
        enableSorting: false,
        cell: ({ row }) => (
          <Badge
            id={`admin.nights.grid.published.${row.original.id}`}
            tone={row.original.published ? 'success' : 'neutral'}
          >
            {row.original.published ? 'Published' : 'Unpublished'}
          </Badge>
        ),
      },
      {
        id: 'ticketsSold',
        header: 'Tickets sold',
        enableSorting: false,
        cell: ({ row }) => (
          <span id={`admin.nights.grid.ticketsSold.${row.original.id}`}>
            {formatInteger(row.original.soldTickets)}
          </span>
        ),
      },
      {
        id: 'guestTickets',
        header: 'Guest tickets',
        enableSorting: false,
        cell: ({ row }) => (
          <span id={`admin.nights.grid.guestTickets.${row.original.id}`}>
            {formatInteger(row.original.guestTickets)}
          </span>
        ),
      },
    ];
    if (showRevenue) {
      cols.push({
        id: 'ticketRevenue',
        header: 'Ticket revenue',
        enableSorting: false,
        cell: ({ row }) => (
          <span id={`admin.nights.grid.ticketRevenue.${row.original.id}`}>
            {formatCents(row.original.ticketRevenueCents)}
          </span>
        ),
      });
    }
    return cols;
  }, [listHref, showRevenue]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
            Nights
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Upcoming nights, the next night first. A night that is
            running now stays until its doors close.
          </p>
        </div>
        <NightsViewToggle layout="grid" />
      </div>

      {error ? (
        <QueryErrorNotice error={error} title="Could not load nights" />
      ) : null}

      <FilterBar
        primary={
          <>
            <Switch
              id="admin.nights.grid.ticketedOnly"
              label="Ticketed only"
              checked={ticketedOnly}
              onChange={(e) =>
                setFilters({ ticketed: e.target.checked ? '' : 'all' })
              }
            />
            <Switch
              id="admin.nights.grid.activeOnly"
              label="Active only"
              checked={activeOnly}
              onChange={(e) =>
                setFilters({ active: e.target.checked ? '' : 'all' })
              }
            />
          </>
        }
      />

      <div id="admin.nights.grid">
        <DataGrid<NightRow>
          data={rows}
          columns={columns}
          loading={loading && !viewData}
          rowId={(row) => row.id}
          rowDomId={(row) => `admin.nights.grid.row.${row.id}`}
          rowMuted={(row) => !row.isActive}
          onRowClick={(row) =>
            navigate(`/nights/${row.id}`, { state: { from: listHref } })
          }
          sorting={{ state: sorting, onChange: handleSortingChange }}
          pagination={{
            page: pageInfo?.page ?? url.page,
            pageSize: pageInfo?.pageSize ?? url.size,
            totalRows: pageInfo?.totalRows ?? 0,
            totalPages: pageInfo?.totalPages ?? 1,
            onPageChange: (next) => patch({ page: next }),
            onPageSizeChange: (next) => setFilters({ size: next }),
          }}
        />
      </div>
    </div>
  );
}
